import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [addons, setAddons] = useState([]);
  const [orderMessage, setOrderMessage] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('sohkinCart');
    const savedAddons = localStorage.getItem('sohkinAddons');
    const savedMessage = localStorage.getItem('sohkinOrderMessage');
    
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error parsing cart:', error);
        setCart([]);
      }
    }
    
    if (savedAddons) {
      try {
        setAddons(JSON.parse(savedAddons));
      } catch (error) {
        console.error('Error parsing addons:', error);
        setAddons([]);
      }
    }
    
    if (savedMessage) {
      setOrderMessage(savedMessage);
    }
    
    setIsInitialized(true);
  }, []);

  // Save to localStorage whenever cart, addons, or orderMessage changes
  useEffect(() => {
    if (isInitialized) {
      try {
        localStorage.setItem('sohkinCart', JSON.stringify(cart));
        localStorage.setItem('sohkinAddons', JSON.stringify(addons));
        localStorage.setItem('sohkinOrderMessage', orderMessage);
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }
  }, [cart, addons, orderMessage, isInitialized]);

  const calculateItemPrice = (item) => {
    let price = item.price || 0;
    if (item.selectedOption) {
      const key = Object.keys(item.selectedOption)[0];
      price += item.selectedOption[key];
    }
    return price;
  };

  const addToCart = (product, customMessage = '') => {
    const existingItem = cart.find(item => 
      item.id === product.id && item.type === product.type
    );

    const finalPrice = calculateItemPrice(product);

    if (existingItem) {
      updateQuantity(product.id, product.type, existingItem.quantity + product.quantity);
    } else {
      setCart(prev => [...prev, {
        ...product,
        customMessage,
        finalPrice,
        selectedOption: product.selectedOption || null,
        addedAt: new Date().toISOString()
      }]);
    }
  };

  const updateQuantity = (id, type, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(id, type);
      return;
    }

    setCart(prev => prev.map(item =>
      item.id === id && item.type === type
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (id, type) => {
    setCart(prev => prev.filter(item => 
      !(item.id === id && item.type === type)
    ));
  };

  const updateCustomMessage = (id, type, message) => {
    setCart(prev => prev.map(item =>
      item.id === id && item.type === type
        ? { ...item, customMessage: message }
        : item
    ));
  };

  const addAddon = (addon) => {
    const existing = addons.find(a => a.id === addon.id);
    if (existing) {
      setAddons(prev => prev.map(a =>
        a.id === addon.id ? { ...a, quantity: a.quantity + 1 } : a
      ));
    } else {
      setAddons(prev => [...prev, { ...addon, quantity: 1 }]);
    }
  };

  const removeAddon = (addonId) => {
    setAddons(prev => prev.filter(a => a.id !== addonId));
  };

  const updateAddonQuantity = (addonId, newQuantity) => {
    if (newQuantity < 1) {
      removeAddon(addonId);
      return;
    }

    setAddons(prev => prev.map(addon =>
      addon.id === addonId ? { ...addon, quantity: newQuantity } : addon
    ));
  };

  const clearCart = () => {
    setCart([]);
    setAddons([]);
    setOrderMessage('');
  };

  const getCartTotal = () => {
    const cartTotal = cart.reduce((total, item) => {
      const itemPrice = item.finalPrice || item.price || 0;
      return total + (itemPrice * item.quantity);
    }, 0);

    const addonsTotal = addons.reduce((total, addon) => {
      return total + (addon.price * addon.quantity);
    }, 0);

    return cartTotal + addonsTotal;
  };

  const getItemCount = () => {
    const cartItems = cart.reduce((total, item) => total + item.quantity, 0);
    const addonItems = addons.reduce((total, addon) => total + addon.quantity, 0);
    return cartItems + addonItems;
  };

  return (
    <CartContext.Provider value={{
      cart,
      addons,
      orderMessage,
      setOrderMessage,
      addToCart,
      updateQuantity,
      removeFromCart,
      updateCustomMessage,
      addAddon,
      removeAddon,
      updateAddonQuantity,
      clearCart,
      getCartTotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};